import React, { useEffect, useRef, useState } from "react";
import { Box, ClickAwayListener, Grow, Paper, Typography, useTheme } from "@mui/material";
import SearchableOptionList from "./SearchableOptionList";
import SelectedChipsArea from "./SelectedChipsArea";
import { rankOptionMatches } from "./optionMatching";

const MAX_VISIBLE_OPTIONS = 50;

/**
 * V4 multi-select: chip/tag search field + options in a floating dropdown.
 * Keeps the contextual filter menu compact (V2-style list presentation).
 */
export default function ChipSearchDropdown({
  id,
  options = [],
  value,
  onChange,
  placeholder = "Search...",
  disabled = false,
  autoFocus = false,
  getOptionLabel,
  getOptionKey,
  emptyLabel = "No matches",
  multiple = true,
  allowCustomValue = false,
  onApply,
  onClear,
  shellSx,
  "data-testid": testId,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const suppressNextFocusOpenRef = useRef(false);
  const [inputValue, setInputValue] = useState("");
  const [listOpen, setListOpen] = useState(false);
  const [inputInvalid, setInputInvalid] = useState(false);
  const selectedValues = Array.isArray(value) ? value : [];

  useEffect(() => {
    if (!autoFocus || disabled) return undefined;
    const timer = window.setTimeout(() => inputRef.current?.focus?.(), 40);
    return () => window.clearTimeout(timer);
  }, [autoFocus, disabled]);

  const openList = () => {
    if (disabled) return;
    setListOpen(true);
  };

  const closeList = () => setListOpen(false);

  const commitOptionSelection = (next) => {
    onChange(next);
    setInputValue("");
    setInputInvalid(false);
    closeList();
    if (document.activeElement !== inputRef.current) {
      suppressNextFocusOpenRef.current = true;
      inputRef.current?.focus?.();
    }
  };

  const commitCustomValue = () => {
    const typed = inputValue.trim();
    if (!typed) return false;
    const keyFn = getOptionKey || getOptionLabel || String;
    const alreadySelected = selectedValues.some(
      (selected) => keyFn(selected) === typed
    );
    if (!alreadySelected) {
      onChange(multiple ? [...selectedValues, typed] : [typed]);
    }
    setInputValue("");
    return true;
  };

  const handleInputKeyDown = (event) => {
    if (event.key === "Escape" && listOpen) {
      event.preventDefault();
      event.stopPropagation();
      closeList();
    } else if (allowCustomValue && event.key === "Enter") {
      // The option list only ever holds values seen on the current page, so
      // without this the user cannot search for anything outside it.
      if (commitCustomValue()) {
        event.preventDefault();
        closeList();
      }
    } else if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();

      const typed = inputValue.trim().toLowerCase();
      if (!typed) {
        setInputInvalid(false);
        closeList();
        onApply?.();
        return;
      }

      const labelFn = getOptionLabel || String;
      const keyFn = getOptionKey || getOptionLabel || String;
      const candidates = [...options];
      const seen = new Set(candidates.map((option) => keyFn(option)));
      selectedValues.forEach((selected) => {
        const key = keyFn(selected);
        if (seen.has(key)) return;
        seen.add(key);
        candidates.unshift(selected);
      });
      const [match] = rankOptionMatches(candidates, typed, labelFn);
      if (!match) {
        setInputInvalid(true);
        openList();
        return;
      }

      const matchKey = keyFn(match);
      const alreadySelected = selectedValues.some(
        (selected) => keyFn(selected) === matchKey
      );
      const resolvedMatch = typeof match === "string" ? match : labelFn(match);
      const next = alreadySelected
        ? selectedValues
        : multiple
          ? [...selectedValues, resolvedMatch]
          : [resolvedMatch];
      commitOptionSelection(next);
      onApply?.();
    } else if (event.key !== "Escape" && event.key !== "Tab") {
      openList();
    }
  };

  return (
    <ClickAwayListener onClickAway={closeList}>
      <Box
        ref={anchorRef}
        sx={{ position: "relative" }}
        data-testid={testId || (id ? `${id}-chip-dropdown` : "chip-search-dropdown")}
      >
        <SelectedChipsArea
          data-testid={id ? `${id}-selected-chips` : "selected-chips-area"}
          values={selectedValues}
          getLabel={getOptionLabel}
          getKey={(option, index) =>
            `${(getOptionKey || getOptionLabel || String)(option)}-${index}`
          }
          onRemove={(item) => {
            const keyFn = getOptionKey || getOptionLabel || String;
            const removeKey = keyFn(item);
            onChange(
              selectedValues.filter((selected) => keyFn(selected) !== removeKey)
            );
          }}
          onClear={() => (onClear ? onClear() : onChange([]))}
          disabled={disabled}
          inputId={id}
          inputRef={inputRef}
          inputValue={inputValue}
          onInputChange={(next) => {
            setInputValue(next);
            setInputInvalid(false);
            openList();
          }}
          onInputBlur={() => setInputInvalid(false)}
          onInputFocus={() => {
            if (suppressNextFocusOpenRef.current) {
              suppressNextFocusOpenRef.current = false;
              return;
            }
            openList();
          }}
          onInputClick={openList}
          onInputKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          shellSx={shellSx}
          inputError={inputInvalid}
          aria-invalid={inputInvalid}
          aria-describedby={inputInvalid ? `${id || "chip"}-input-error` : undefined}
          aria-expanded={listOpen}
          aria-controls={listOpen ? `${id || "chip"}-options` : undefined}
        />

        <Grow in={listOpen} timeout={{ enter: 140, exit: 100 }} unmountOnExit>
          <Paper
            id={`${id || "chip"}-options`}
            elevation={0}
            role="presentation"
            onMouseDown={(event) => {
              // Keep input focus while toggling options.
              event.preventDefault();
            }}
            sx={{
              position: "relative",
              width: "100%",
              mt: 0.5,
              maxHeight: 160,
              overflow: "hidden",
              borderRadius: "12px",
              border: `1px solid ${
                isDark ? "rgba(255,255,255,0.12)" : "rgba(88,89,91,0.14)"
              }`,
              backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
              boxShadow: isDark
                ? "0 12px 28px rgba(0,0,0,0.55)"
                : "0 12px 28px rgba(23,15,74,0.14)",
            }}
          >
            <Box sx={{ p: 0.75, maxHeight: 160, overflow: "auto" }}>
              <SearchableOptionList
                multiple={multiple}
                hideSearch
                query={inputValue}
                options={options}
                value={selectedValues}
                onChange={commitOptionSelection}
                getOptionLabel={getOptionLabel}
                getOptionKey={getOptionKey}
                emptyLabel={emptyLabel}
                autoFocus={false}
                placeholder={placeholder}
                maxVisibleOptions={MAX_VISIBLE_OPTIONS}
              />
            </Box>
          </Paper>
        </Grow>
        {inputInvalid ? (
          <Typography
            id={`${id || "chip"}-input-error`}
            role="alert"
            sx={{ mt: 0.5, color: "error.main", fontSize: 12, lineHeight: 1.3 }}
          >
            Choose a matching option.
          </Typography>
        ) : null}
      </Box>
    </ClickAwayListener>
  );
}
